import React, { useState, useEffect } from 'react';
import { UserPlus, User, Activity, MessageSquare, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Label } from '../ui/label';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const GhostAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPostModal, setShowPostModal] = useState(false);
  const [selectedGhost, setSelectedGhost] = useState(null);
  const [postContent, setPostContent] = useState('');
  const [newGhost, setNewGhost] = useState({
    name: '',
    email: '',
    title: '',
    bio: '',
    user_type: 'trainer'
  });

  useEffect(() => {
    fetchGhostAccounts();
  }, []);

  const fetchGhostAccounts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/ghost-accounts`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAccounts(response.data.accounts || []);
    } catch (error) {
      console.error('Error fetching ghost accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGhost = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/api/admin/ghost-accounts`,
        newGhost,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowCreateModal(false);
      fetchGhostAccounts();
      alert('Ghost account created successfully!');
      setNewGhost({ name: '', email: '', title: '', bio: '', user_type: 'trainer' });
    } catch (error) {
      console.error('Error creating ghost account:', error);
      alert('Failed to create ghost account');
    }
  };

  const handlePostAsGhost = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/api/admin/ghost-accounts/${selectedGhost.id}/post`,
        null,
        {
          params: { content: postContent },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setShowPostModal(false);
      setPostContent('');
      alert('Post created successfully!');
    } catch (error) {
      console.error('Error posting as ghost:', error);
      alert('Failed to create post');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading ghost accounts...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Ghost Account Management</CardTitle>
            <Button
              className="bg-purple-600 hover:bg-purple-700"
              onClick={() => setShowCreateModal(true)}
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Create Ghost Account
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Ghost Accounts Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {accounts.map((ghost) => (
          <Card key={ghost.id} className="hover:shadow-lg transition-shadow border-purple-200">
            <CardContent className="p-6">
              <div className="flex items-start space-x-4 mb-4">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center">
                  <User className="w-8 h-8 text-purple-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-lg">{ghost.name}</h3>
                  <p className="text-sm text-gray-600">{ghost.title}</p>
                  <p className="text-xs text-purple-600 mt-1">{ghost.user_type}</p>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-4 line-clamp-3">{ghost.bio}</p>

              <div className="grid grid-cols-3 gap-2 mb-4 text-center text-xs">
                <div className="bg-blue-50 p-2 rounded">
                  <FileText className="w-4 h-4 mx-auto text-blue-600 mb-1" />
                  <p className="font-semibold">{ghost.stats?.posts || 0}</p>
                  <p className="text-gray-600">Posts</p>
                </div>
                <div className="bg-green-50 p-2 rounded">
                  <MessageSquare className="w-4 h-4 mx-auto text-green-600 mb-1" />
                  <p className="font-semibold">{ghost.stats?.comments || 0}</p>
                  <p className="text-gray-600">Comments</p>
                </div>
                <div className="bg-purple-50 p-2 rounded">
                  <Activity className="w-4 h-4 mx-auto text-purple-600 mb-1" />
                  <p className="font-semibold">Active</p>
                </div>
              </div>

              <Button
                className="w-full bg-purple-600 hover:bg-purple-700"
                onClick={() => {
                  setSelectedGhost(ghost);
                  setShowPostModal(true);
                }}
              >
                Post as {ghost.name.split(' ')[0]}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create Ghost Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Create Ghost Account</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                placeholder="Dr. Sarah Johnson"
                value={newGhost.name}
                onChange={(e) => setNewGhost({...newGhost, name: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                placeholder="sarah.johnson@example.com"
                value={newGhost.email}
                onChange={(e) => setNewGhost({...newGhost, email: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                placeholder="Leadership Development Expert"
                value={newGhost.title}
                onChange={(e) => setNewGhost({...newGhost, title: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="bio">Bio *</Label>
              <Textarea
                id="bio"
                placeholder="Passionate about transforming organizational culture..."
                rows={4}
                value={newGhost.bio}
                onChange={(e) => setNewGhost({...newGhost, bio: e.target.value})}
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button className="bg-purple-600 hover:bg-purple-700" onClick={handleCreateGhost}>
                Create Ghost Account
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Post as Ghost Modal */}
      <Dialog open={showPostModal} onOpenChange={setShowPostModal}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Post as {selectedGhost?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Textarea
              placeholder="What would you like to share?"
              rows={6}
              value={postContent}
              onChange={(e) => setPostContent(e.target.value)}
            />
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowPostModal(false)}>
                Cancel
              </Button>
              <Button className="bg-purple-600 hover:bg-purple-700" onClick={handlePostAsGhost}>
                Post
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GhostAccounts;